import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Uploads Modal - React Implementation
 *
 * Handles photo uploads for structures/UPRNs with:
 * - Gallery view of existing uploads
 * - File upload via camera or file picker
 * - Tag management
 * - Delete functionality
 * - Clipboard copy (low-res/high-res)
 *
 * Integrates with legacy JavaScript system for file uploads (select-object.js)
 * which handles the actual upload to server with proper context.
 */
import { useRef, useEffect, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, } from '../../../../components/ui/dialog';
import { Loader2, Camera, Upload as UploadIcon } from 'lucide-react';
import { UploadGallery } from '../UploadGallery';
import { useUploads } from '../../hooks/useUploads';
const UploadsModalReact = ({ isOpen, onClose }) => {
    const cameraInputRef = useRef(null);
    const uploadInputRef = useRef(null);
    const [objectInfo, setObjectInfo] = useState(null);
    /**
     * Get the selectObject control from the legacy CFN global
     */
    const getSelectObject = () => {
        return window.CFN?.selectObject;
    };
    // Update object info when modal opens
    useEffect(() => {
        if (isOpen) {
            const selectObject = getSelectObject();
            if (selectObject?.currentObject) {
                const objType = selectObject.objectType();
                setObjectInfo({
                    type: objType === 'UPRN' ? 'UPRN' : 'structure',
                    id: selectObject.objectID(),
                    title: selectObject.objectTitle(),
                    key: selectObject.objectKey(),
                });
            }
            else {
                setObjectInfo(null);
            }
        }
    }, [isOpen]);
    // Use the uploads hook when we have object info
    const { uploads, pendingUploads, isLoading, error, fetchUploads, addPendingUpload, updatePendingUpload, completePendingUpload, removePendingUpload, deleteUpload, addTag, removeTag, } = useUploads({
        objectType: objectInfo?.type || 'structure',
        objectId: objectInfo?.id || '',
        autoFetch: false, // We'll fetch manually when modal opens
    });
    // Fetch uploads when modal opens with valid object
    useEffect(() => {
        if (isOpen && objectInfo?.id) {
            fetchUploads();
        }
    }, [isOpen, objectInfo?.id, fetchUploads]);
    // Sync pending uploads from legacy system
    useEffect(() => {
        if (!isOpen || !objectInfo?.key)
            return;
        const selectObject = getSelectObject();
        if (!selectObject?.pendingUploads)
            return;
        const legacyPending = selectObject.pendingUploads[objectInfo.key];
        if (!legacyPending)
            return;
        // Convert legacy pending uploads to our format
        Object.values(legacyPending).forEach((asset) => {
            if (!asset.id) {
                // Still pending
                addPendingUpload({
                    filename: asset.filename,
                    size: asset.size,
                    mimetype: asset.mimetype,
                    lastModified: asset.lastModified,
                    data: asset.data,
                    seq: asset.seq,
                });
            }
        });
    }, [isOpen, objectInfo?.key, addPendingUpload]);
    // Listen for upload events from legacy system
    useEffect(() => {
        if (!isOpen)
            return;
        const handleUploadComplete = (event) => {
            const { filename, upload } = event.detail;
            completePendingUpload(filename, upload);
        };
        const handleUploadProgress = (event) => {
            const { filename, data } = event.detail;
            updatePendingUpload(filename, { data });
        };
        const handleUploadsRefresh = () => {
            // Refresh uploads from server
            fetchUploads();
        };
        window.addEventListener('cfn:upload:complete', handleUploadComplete);
        window.addEventListener('cfn:upload:progress', handleUploadProgress);
        window.addEventListener('cfn:uploads:refresh', handleUploadsRefresh);
        return () => {
            window.removeEventListener('cfn:upload:complete', handleUploadComplete);
            window.removeEventListener('cfn:upload:progress', handleUploadProgress);
            window.removeEventListener('cfn:uploads:refresh', handleUploadsRefresh);
        };
    }, [isOpen, completePendingUpload, updatePendingUpload, fetchUploads]);
    /**
     * Handle file selection by delegating to the legacy upload system.
     * The legacy system (select-object.js) has context about the current object
     * and handles AJAX uploads with proper objectType/objectID.
     */
    const handleFilesSelected = useCallback((event) => {
        const selectObject = getSelectObject();
        if (!selectObject?.currentObject) {
            console.error('[UploadsModal] No object selected - cannot upload files');
            alert('Please select a structure or UPRN on the map first');
            event.target.value = '';
            return;
        }
        const files = event.target.files;
        if (!files || files.length === 0)
            return;
        // Add pending uploads to React state for immediate UI feedback
        Array.from(files).forEach((file, idx) => {
            const isImage = file.type.startsWith('image/');
            const pending = {
                filename: file.name,
                size: file.size,
                mimetype: file.type,
                lastModified: file.lastModified,
                seq: pendingUploads.length + idx,
            };
            addPendingUpload(pending);
            // Read image data for preview
            if (isImage) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result) {
                        updatePendingUpload(file.name, { data: e.target.result });
                    }
                };
                reader.readAsDataURL(file);
            }
        });
        // Delegate to legacy upload handler which does the actual upload
        if (typeof selectObject.newFiles === 'function') {
            selectObject.newFiles(event.nativeEvent);
        }
        else {
            console.error('[UploadsModal] Legacy newFiles handler not available');
            alert('Upload system not initialised. Please refresh the page.');
        }
        // Reset input to allow re-uploading the same file
        event.target.value = '';
    }, [addPendingUpload, updatePendingUpload, pendingUploads.length]);
    const handleCameraClick = () => {
        if (!getSelectObject()?.currentObject) {
            alert('Please select a structure or UPRN on the map first');
            return;
        }
        cameraInputRef.current?.click();
    };
    const handleUploadClick = () => {
        if (!getSelectObject()?.currentObject) {
            alert('Please select a structure or UPRN on the map first');
            return;
        }
        uploadInputRef.current?.click();
    };
    // Sync with hash for back button support
    useEffect(() => {
        if (isOpen) {
            if (window.location.hash !== '#gallery') {
                window.history.pushState(null, '', '#gallery');
            }
        }
    }, [isOpen]);
    useEffect(() => {
        const handlePopState = () => {
            if (isOpen && window.location.hash !== '#gallery') {
                onClose();
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isOpen, onClose]);
    // Update hash when closing
    const handleClose = () => {
        if (window.location.hash === '#gallery') {
            window.history.back();
        }
        onClose();
    };
    // Handle delete
    const handleDelete = async (uploadId) => {
        const success = await deleteUpload(uploadId);
        return success;
    };
    // Handle add tag
    const handleAddTag = async (uploadId, tag) => {
        return await addTag(uploadId, tag);
    };
    // Handle remove tag
    const handleRemoveTag = async (uploadId, tag) => {
        return await removeTag(uploadId, tag);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Dialog, { open: isOpen, onOpenChange: (open) => !open && handleClose(), children: _jsxs(DialogContent, { className: "max-w-5xl max-h-[85vh] flex flex-col", children: [_jsx(DialogHeader, { children: _jsxs(DialogTitle, { className: "flex items-center gap-4", children: [_jsx("span", { children: objectInfo?.title || 'Photo Uploads' }), _jsxs("div", { className: "flex gap-3 ml-auto", children: [_jsx("button", { type: "button", onClick: handleCameraClick, className: "text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-muted", title: "Take Picture", disabled: !objectInfo, children: _jsx(Camera, { className: "h-6 w-6" }) }), _jsx("button", { type: "button", onClick: handleUploadClick, className: "text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-muted", title: "Upload Pictures", disabled: !objectInfo, children: _jsx(UploadIcon, { className: "h-6 w-6" }) })] })] }) }), _jsx("div", { className: "flex-1 overflow-auto min-h-0", children: !objectInfo ? (_jsxs("div", { className: "flex flex-col items-center justify-center py-12 text-muted-foreground", children: [_jsx("p", { className: "text-lg font-medium", children: "No object selected" }), _jsx("p", { className: "text-sm mt-1", children: "Please select a structure or UPRN on the map first" })] })) : isLoading ? (_jsxs("div", { className: "flex items-center justify-center py-12", children: [_jsx(Loader2, { className: "h-8 w-8 animate-spin text-muted-foreground" }), _jsx("span", { className: "ml-2 text-muted-foreground", children: "Loading uploads..." })] })) : error ? (_jsxs("div", { className: "flex flex-col items-center justify-center py-12 text-destructive", children: [_jsx("p", { className: "text-lg font-medium", children: "Error loading uploads" }), _jsx("p", { className: "text-sm mt-1", children: error }), _jsx("button", { onClick: fetchUploads, className: "mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90", children: "Retry" })] })) : (_jsx(UploadGallery, { uploads: uploads, pendingUploads: pendingUploads, onDelete: handleDelete, onAddTag: handleAddTag, onRemoveTag: handleRemoveTag })) })] }) }), _jsx("input", { id: "ModalCameraInput", ref: cameraInputRef, type: "file", accept: "image/*", capture: "environment", onChange: handleFilesSelected, style: { display: 'none' } }), _jsx("input", { id: "ModalUploadInput", ref: uploadInputRef, type: "file", accept: "image/*, video/*, application/pdf", multiple: true, onChange: handleFilesSelected, style: { display: 'none' } })] }));
};
export default UploadsModalReact;
